import { gql, QueryResult } from '@apollo/client'
import * as React from 'react'
import { Link } from '../../../../common/src/components/Link'
import { Spacer, H3, IntroText, BodyText } from '../../../../common/src/components/core'
import { FetchUserDetailsQueryVariables, FetchUserDetailsQuery } from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'
import { Fragments } from '../../graphql/fragments'
import EditHeader from '../components/EditHeader'
import { PollingQuery } from '../components/PollingQuery'
import UserEditBoxes from './UserEditBoxes'
import UserEditBoxesADP from './UserEditBoxesADP'

const fetchUserDetails = gql`
  query FetchUserDetails($userId: Int!) {
    user(userId: $userId) {
      ...UserAdminDetails
    }
  }
  ${Fragments.userAdminDetails}
`

const updateUserMutation = gql`
  mutation UpdateUser($user: UserInput!) {
    updateUser(user: $user) {
      ...UserAdminDetails
    }
  }
  ${Fragments.userAdminDetails}
`

class UserDetailImpl extends React.PureComponent<
  FetchUserDetailsQueryVariables & QueryResult<FetchUserDetailsQuery, FetchUserDetailsQueryVariables>
> {
  render() {
    const { data } = this.props
    if (data == null || data.user == null) {
      return null
    }

    // show ADP config for part-time employees — they're the only ones who we need ADP info for
    const employeeWorksPartTime = ['OPERATOR', 'SENIOR_OPERATOR', 'ANALYST'].includes(data.user.userType)

    return (
      <div>
        <EditHeader header={data.user.name || data.user.email} onEdit={this.onEditBound} />
        <Spacer height={32} />
        <UserEditBoxes user={data.user} onPatch={this.onPatchBound} />
        {employeeWorksPartTime && (
          <>
            <Spacer height={48} />
            <H3>ADP</H3>
            <Spacer height={12} />
            <IntroText>
              We pay employees using ADP, a payroll tool. ADP identifies each employee by full legal name. To pay them
              automatically, enter their legal name below.
            </IntroText>
            <Spacer height={24} />
            <UserEditBoxesADP user={data.user} onPatch={this.onPatchBound} />
            <Spacer height={16} />
            <BodyText>
              If the employee gender-transitions, their name in ADP might be their{' '}
              <Link to={'https://en.wikipedia.org/wiki/Deadnaming'} color="silver">
                “dead name”
              </Link>
              . We only use this legal name for ADP payroll. Each employee can use their preferred name in Slack, CC,
              and all other communication.
            </BodyText>
            <Spacer height={8} />
            <BodyText>We try to sync with ADP each night at 8:15pm Pacific.</BodyText>
          </>
        )}
      </div>
    )
  }

  private onEditBound = (name: string) => this.onPatchBound({ name })

  private onPatchBound = (patch: any) => {
    const { data } = this.props
    const { user } = data!
    if (user == null) {
      return Promise.resolve(null)
    }
    const expectedModel = Object.assign({}, user, patch)
    delete expectedModel.__typename // eslint-disable-line
    const permissions = expectedModel.permissions
    delete expectedModel.permissions

    return apolloWebClient.mutate({
      mutation: updateUserMutation,
      variables: {
        user: expectedModel,
      },
      optimisticResponse: {
        updateUser: {
          __typename: 'User',
          permissions,
          ...expectedModel,
        },
      },
    })
  }
}

export default function UserDetail(props: FetchUserDetailsQueryVariables) {
  return (
    <PollingQuery query={fetchUserDetails} variables={props}>
      {(res: QueryResult<FetchUserDetailsQuery, FetchUserDetailsQueryVariables>) => (
        <UserDetailImpl {...props} {...res} />
      )}
    </PollingQuery>
  )
}
